import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { initGlobalFormNav, initGlobalUppercase } from "@/lib/keyboardNav";

initGlobalFormNav();
initGlobalUppercase();
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MinimizedProvider } from "@/context/MinimizedContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Vehicles from "@/pages/Vehicles";
import Parts from "@/pages/Parts";
import JobCards from "@/pages/JobCards";
import Invoices from "@/pages/Invoices";
import AIInsights from "@/pages/AIInsights";
import CustomerPortal from "@/pages/CustomerPortal";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Reminders from "@/pages/Reminders";
import BarcodeLabels from "@/pages/BarcodeLabels";
import JobRates from "@/pages/JobRates";
import Packages from "@/pages/Packages";
import Bookings from "@/pages/Bookings";
import VehicleDetail from "@/pages/VehicleDetail";
import EstimateMaster from "@/pages/EstimateMaster";
import Challans from "@/pages/Challans";
import SectorMaster from "@/pages/SectorMaster";
import WarrantyMaster from "@/pages/WarrantyMaster";
import WarrantyClaims from "@/pages/WarrantyClaims";
import ItemMaster from "@/pages/ItemMaster";
import PrintTemplateForm from "@/pages/PrintTemplateForm";
import PrintTemplates from "@/pages/PrintTemplates";
import { OrgBackupPage, OrgRestorePage } from "@/pages/OrgBackup";
import Transactions from "@/pages/Transactions";
import ReorderPurchase from "@/pages/ReorderPurchase";
import SaleReturn from "@/pages/SaleReturn";
import PurchaseReturn from "@/pages/PurchaseReturn";
import ItemRegister from "@/pages/ItemRegister";
import ItemRegisterDetail from "@/pages/ItemRegisterDetail";
import { SettingsFinancial, SettingsTransaction, SettingsPrint, SettingsNotification, SettingsLanguage } from "@/pages/Settings";
import StockSettings from "@/pages/StockSettings";
import JobTypeMaster from "@/pages/JobTypeMaster";
import { I18nProvider } from "@/context/I18nContext";
import CompanyMaster from "@/pages/CompanyMaster";
import CategoryMaster from "@/pages/CategoryMaster";
import StoreMaster from "@/pages/StoreMaster";
import GroupMaster from "@/pages/GroupMaster";
import HsnMaster from "@/pages/HsnMaster";
import RateMaster from "@/pages/RateMaster";
import SupplierCompany from "@/pages/SupplierCompany";
import { SalesReport, PurchaseReport, StockReport, WorkshopReport, FinancialReport } from "@/pages/ReportsPages";
import EmployeeMaster from "@/pages/EmployeeMaster";
import Attendance from "@/pages/Attendance";
import LeaveManagement from "@/pages/LeaveManagement";
import Payroll from "@/pages/Payroll";
import SalarySlip from "@/pages/SalarySlip";
import HRMSDepartments from "@/pages/HRMSDepartments";
import HRMSDesignations from "@/pages/HRMSDesignations";
import GeoAttendance from "@/pages/hr/GeoAttendance";
import GeoFenceConfig from "@/pages/hr/GeoFenceConfig";
import AllStock from "@/pages/AllStock";
import StoreWiseStock from "@/pages/StoreWiseStock";
import BatchWiseStock from "@/pages/BatchWiseStock";
import BatchWiseStockOpening from "@/pages/BatchWiseStockOpening";
import StockTransfer from "@/pages/StockTransfer";
import StockStatus from "@/pages/StockStatus";
import DayBook from "@/pages/DayBook";
import CashBook from "@/pages/CashBook";
import BankBook from "@/pages/BankBook";
import CustomerLedger from "@/pages/CustomerLedger";
import Ledger from "@/pages/Ledger";
import LabelCreation from "@/pages/LabelCreation";
import SupplierMaster from "@/pages/SupplierMaster";
import DebtorMaster from "@/pages/DebtorMaster";
import Expenses from "@/pages/Expenses";
import Vouchers from "@/pages/Vouchers";
import GstReports from "@/pages/GstReports";
import ProfitLoss from "@/pages/ProfitLoss";
import CustomerMaster from "@/pages/CustomerMaster";
import Feedback from "@/pages/Feedback";
import Campaigns from "@/pages/Campaigns";
import Organisation from "@/pages/Organisation";
import UsersMaster from "@/pages/UsersMaster";
import RolesMaster from "@/pages/RolesMaster";
import PermissionsMaster from "@/pages/PermissionsMaster";
import OrganisationAuthorisation from "@/pages/OrganisationAuthorisation";
import MasterAuthorisation from "@/pages/MasterAuthorisation";
import ManageMasters from "@/pages/ManageMasters";
import ActiveOrganisations from "@/pages/ActiveOrganisations";
import PlanMaster from "@/pages/PlanMaster";
import Register from "@/pages/Register";
import AdminDashboard from "@/pages/AdminDashboard";
import PasswordMaster from "@/pages/PasswordMaster";
import AdminUserForm from "@/pages/AdminUserForm";
import AdminUserAuthorisation from "@/pages/AdminUserAuthorisation";
import Establishment, { EstablishmentUsers, EstablishmentRoles, EstablishmentPermissions } from "@/pages/Establishment";
import OrganisationProfile from "@/pages/OrganisationProfile";
import { AdminRolesList, AdminRoleForm } from "@/pages/AdminRoles";
import { AdminEstablishmentsList, AdminEstablishmentForm } from "@/pages/AdminEstablishments";
import { AdminOrgUsersList, AdminOrgUserForm } from "@/pages/AdminOrgUsers";
import AdminBackup from "@/pages/AdminBackup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LocalServer from "@/pages/LocalServer";
import LocalSetup from "@/pages/LocalSetup";
import { useLocalPublicStatus } from "@/components/SyncIndicator";
import { menuKeyOf, isMatrixDrivenRole } from "@/lib/menuKeys";

function PlatformAdminGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_platform_admin) return <Navigate to="/" replace />;
  return children;
}

function Guarded({ children, roles }) {
  const { user, loading, isSuperAdmin, can } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Hardcoded: SaaS Admin can open every page regardless of route role gates.
  // Matrix-driven roles (mechanic + custom roles) bypass the static role gate —
  // their access is decided by the permission matrix check below.
  if (roles && !roles.includes(user.role) && !isSuperAdmin && !isMatrixDrivenRole(user.role)) return <Navigate to="/" replace />;
  // Matrix-driven roles: page access follows the Role & Permission Master matrix
  // (no View permission on the mapped menu = page blocked). Other roles unchanged.
  // "/" is exempt — HomeRedirect decides the landing page there.
  if (!isSuperAdmin && isMatrixDrivenRole(user.role)) {
    const path = window.location.pathname;
    const k = path === "/" ? null : menuKeyOf(path);
    if (k && !can(k, "view")) return <Navigate to="/" replace />;
  }
  return children;
}

// Shop server not linked yet: everything goes to the setup wizard.
function LocalSetupGate() {
  const st = useLocalPublicStatus();
  const { pathname } = useLocation();
  if (st?.mode === "local" && st.setup_required && pathname !== "/local-setup") return <Navigate to="/local-setup" replace />;
  return null;
}

function HomeRedirect() {
  const { user, can } = useAuth();
  if (user?.is_platform_admin) return <Navigate to="/admin" replace />;
  if (user?.role === "customer") return <Navigate to="/portal" replace />;
  // Matrix-driven roles (mechanic + custom roles): land on the first page the
  // role matrix actually allows (avoids redirect loops and unauthorised dashboards).
  if (user && isMatrixDrivenRole(user.role)) {
    const first = ["/jobcards", "/", "/vehicles", "/customer-master", "/stocks/all", "/invoices", "/bookings"]
      .find(p => { const k = menuKeyOf(p); return k && can(k, "view"); });
    if (first === "/") return <Dashboard />;
    if (first) return <Navigate to={first} replace />;
    return <div className="p-10 text-sm uppercase tracking-[0.2em] text-muted-foreground" data-testid="matrix-role-no-access">No page access assigned — contact your administrator.</div>;
  }
  return <Dashboard />;
}

function App() {
  return (
    <AuthProvider>
      <MinimizedProvider>
      <I18nProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <LocalSetupGate />
        <Routes>
          <Route path="/local-setup" element={<LocalSetup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route element={<Guarded><Layout /></Guarded>}>
            <Route index element={<HomeRedirect />} />
            <Route path="/establishment" element={<Guarded roles={["owner", "manager"]}><Establishment /></Guarded>} />
            <Route path="/organisation-profile" element={<Guarded roles={["owner", "manager"]}><OrganisationProfile /></Guarded>} />
            <Route path="/establishment/users" element={<Guarded roles={["owner", "manager"]}><EstablishmentUsers /></Guarded>} />
            <Route path="/establishment/roles" element={<Guarded roles={["owner", "manager"]}><EstablishmentRoles /></Guarded>} />
            <Route path="/establishment/permissions" element={<Guarded roles={["owner", "manager"]}><EstablishmentPermissions /></Guarded>} />
            <Route path="/admin" element={<PlatformAdminGuard><Outlet /></PlatformAdminGuard>}>
              <Route index element={<AdminDashboard />} />
              <Route path="admin-users" element={<AdminDashboard />} />
              <Route path="admin-user-auth" element={<AdminUserAuthorisation />} />
              <Route path="users/new" element={<AdminUserForm />} />
              <Route path="users/:uid/edit" element={<AdminUserForm />} />
              <Route path="admin-roles" element={<AdminRolesList />} />
              <Route path="admin-roles/new" element={<AdminRoleForm />} />
              <Route path="admin-roles/:rid/edit" element={<AdminRoleForm />} />
              <Route path="organisation" element={<Organisation />} />
              <Route path="establishments" element={<AdminEstablishmentsList />} />
              <Route path="establishments/new" element={<AdminEstablishmentForm />} />
              <Route path="establishments/:bid/edit" element={<AdminEstablishmentForm />} />
              <Route path="org-users" element={<AdminOrgUsersList />} />
              <Route path="org-users/new" element={<AdminOrgUserForm />} />
              <Route path="org-users/:uid/edit" element={<AdminOrgUserForm />} />
              <Route path="backup" element={<AdminBackup />} />
              <Route path="users-master" element={<UsersMaster />} />
              <Route path="roles-master" element={<RolesMaster />} />
              <Route path="permissions-master" element={<PermissionsMaster />} />
              <Route path="master-auth" element={<MasterAuthorisation />} />
              <Route path="master-auth/:bid" element={<ManageMasters />} />
              <Route path="active-organisations" element={<ActiveOrganisations />} />
              <Route path="password-master" element={<PasswordMaster />} />
              <Route path="plan-master" element={<PlanMaster />} />
              <Route path="organisation-auth" element={<OrganisationAuthorisation />} />
            </Route>
            {/* Legacy paths → send everyone into the admin panel; PlatformAdminGuard bounces non-admins home. */}
            <Route path="/organisation" element={<Navigate to="/admin/organisation" replace />} />
            <Route path="/users-master" element={<Navigate to="/admin/users-master" replace />} />
            <Route path="/roles-master" element={<Navigate to="/admin/roles-master" replace />} />
            <Route path="/permissions-master" element={<Navigate to="/admin/permissions-master" replace />} />
            <Route path="/password-master" element={<Navigate to="/admin/password-master" replace />} />
            <Route path="/plan-master" element={<Navigate to="/admin/plan-master" replace />} />
            <Route path="/organisation-auth" element={<Navigate to="/admin/organisation-auth" replace />} />
            <Route path="/jobcards" element={<JobCards />} />
            <Route path="/sale-return" element={<Guarded roles={["owner", "manager", "accountant"]}><SaleReturn /></Guarded>} />
            <Route path="/purchase-return" element={<Guarded roles={["owner", "manager", "accountant"]}><PurchaseReturn /></Guarded>} />
            <Route path="/bookings" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><Bookings /></Guarded>} />
            <Route path="/estimates" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><EstimateMaster /></Guarded>} />
            <Route path="/challans" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><Challans /></Guarded>} />
            <Route path="/sectors" element={<Guarded roles={["owner", "manager"]}><SectorMaster /></Guarded>} />
            <Route path="/warranties" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><WarrantyMaster /></Guarded>} />
            <Route path="/warranty-claims" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><WarrantyClaims /></Guarded>} />
            <Route path="/jobrates" element={<Guarded roles={["owner", "manager"]}><JobRates /></Guarded>} />
            <Route path="/items" element={<Guarded roles={["owner", "manager"]}><ItemMaster /></Guarded>} />
            <Route path="/companies" element={<Guarded roles={["owner", "manager"]}><CompanyMaster /></Guarded>} />
            <Route path="/categories" element={<Guarded roles={["owner", "manager"]}><CategoryMaster /></Guarded>} />
            <Route path="/groups" element={<Guarded roles={["owner", "manager"]}><GroupMaster /></Guarded>} />
            <Route path="/rates" element={<Guarded roles={["owner", "manager"]}><RateMaster /></Guarded>} />
            <Route path="/supplier-companies" element={<Guarded roles={["owner", "manager"]}><SupplierCompany /></Guarded>} />
            <Route path="/stocks" element={<Navigate to="/stocks/all" replace />} />
            <Route path="/stocks/all" element={<Guarded roles={["owner", "manager", "cashier", "receptionist", "mechanic"]}><AllStock /></Guarded>} />
            <Route path="/stocks/store-wise" element={<Guarded roles={["owner", "manager", "cashier", "receptionist", "mechanic"]}><StoreWiseStock /></Guarded>} />
            <Route path="/stocks/batches" element={<Guarded roles={["owner", "manager", "cashier", "receptionist", "mechanic"]}><BatchWiseStock /></Guarded>} />
            <Route path="/stocks/batch-opening" element={<Guarded roles={["owner", "manager", "cashier", "receptionist", "mechanic"]}><BatchWiseStockOpening /></Guarded>} />
            <Route path="/stocks/transfer" element={<Guarded roles={["owner", "manager"]}><StockTransfer /></Guarded>} />
            <Route path="/stocks/stock-status" element={<Guarded roles={["owner", "manager", "cashier", "receptionist", "mechanic"]}><StockStatus /></Guarded>} />
            <Route path="/hsn" element={<Guarded roles={["owner", "manager"]}><HsnMaster /></Guarded>} />
            <Route path="/reports/sales" element={<Guarded roles={["owner", "manager"]}><SalesReport /></Guarded>} />
            <Route path="/reports/purchase" element={<Guarded roles={["owner", "manager"]}><PurchaseReport /></Guarded>} />
            <Route path="/reports/stock" element={<Guarded roles={["owner", "manager"]}><StockReport /></Guarded>} />
            <Route path="/reports/workshop" element={<Guarded roles={["owner", "manager"]}><WorkshopReport /></Guarded>} />
            <Route path="/reports/financial" element={<Guarded roles={["owner", "manager"]}><FinancialReport /></Guarded>} />
            <Route path="/hr/employees" element={<Guarded roles={["owner", "manager"]}><EmployeeMaster /></Guarded>} />
            <Route path="/hr/attendance" element={<Guarded roles={["owner", "manager"]}><Attendance /></Guarded>} />
            <Route path="/hr/leaves" element={<Guarded roles={["owner", "manager"]}><LeaveManagement /></Guarded>} />
            <Route path="/hr/payroll" element={<Guarded roles={["owner", "manager"]}><Payroll /></Guarded>} />
            <Route path="/hr/salary-slip" element={<Guarded roles={["owner", "manager"]}><SalarySlip /></Guarded>} />
            <Route path="/hr/departments" element={<Guarded roles={["owner", "manager"]}><HRMSDepartments /></Guarded>} />
            <Route path="/hr/designations" element={<Guarded roles={["owner", "manager"]}><HRMSDesignations /></Guarded>} />
            <Route path="/hr/geo-attendance" element={<Guarded roles={["owner", "manager", "mechanic", "cashier", "receptionist"]}><GeoAttendance /></Guarded>} />
            <Route path="/hr/geo-fence" element={<Guarded roles={["owner", "manager"]}><GeoFenceConfig /></Guarded>} />
            <Route path="/reports/geo-attendance" element={<Guarded roles={["owner", "manager"]}><GeoAttendance /></Guarded>} />
            <Route path="/stores" element={<Guarded roles={["owner", "manager"]}><StoreMaster /></Guarded>} />
            <Route path="/day-book" element={<Guarded roles={["owner", "manager"]}><DayBook /></Guarded>} />
            <Route path="/cash-book" element={<Guarded roles={["owner", "manager"]}><CashBook /></Guarded>} />
            <Route path="/bank-book" element={<Guarded roles={["owner", "manager"]}><BankBook /></Guarded>} />
            <Route path="/customer-ledger" element={<Guarded roles={["owner", "manager"]}><CustomerLedger /></Guarded>} />
            <Route path="/vendor-ledger" element={<Navigate to="/ledger" replace />} />
            <Route path="/ledger" element={<Guarded roles={["owner", "manager"]}><Ledger /></Guarded>} />
            <Route path="/label-creation" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><LabelCreation /></Guarded>} />
            <Route path="/supplier-master" element={<Guarded roles={["owner", "manager"]}><SupplierMaster /></Guarded>} />
            <Route path="/debtor-master" element={<Guarded roles={["owner", "manager"]}><DebtorMaster /></Guarded>} />
            <Route path="/expenses" element={<Guarded roles={["owner", "manager"]}><Expenses /></Guarded>} />
            <Route path="/vouchers" element={<Guarded roles={["owner", "manager"]}><Vouchers /></Guarded>} />
            <Route path="/gst-reports" element={<Guarded roles={["owner", "manager"]}><GstReports /></Guarded>} />
            <Route path="/profit-loss" element={<Guarded roles={["owner"]}><ProfitLoss /></Guarded>} />
            <Route path="/customer-master" element={<CustomerMaster />} />
            <Route path="/feedback" element={<Guarded roles={["owner", "manager"]}><Feedback /></Guarded>} />
            <Route path="/campaigns" element={<Guarded roles={["owner", "manager"]}><Campaigns /></Guarded>} />
            <Route path="/reorder-purchase" element={<Guarded roles={["owner", "manager"]}><ReorderPurchase /></Guarded>} />
            <Route path="/transactions" element={<Guarded roles={["owner", "manager", "cashier", "receptionist", "accountant"]}><Transactions /></Guarded>} />
            <Route path="/item-register" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><ItemRegister /></Guarded>} />
            <Route path="/item-register/:itemId" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><ItemRegisterDetail /></Guarded>} />
            <Route path="/settings" element={<Navigate to="/settings/financial" replace />} />
            <Route path="/settings/stock" element={<Guarded roles={["owner", "manager"]}><StockSettings /></Guarded>} />
            <Route path="/settings/job-types" element={<Guarded roles={["owner", "manager"]}><JobTypeMaster /></Guarded>} />
            <Route path="/settings/financial" element={<Guarded roles={["owner"]}><SettingsFinancial /></Guarded>} />
            <Route path="/settings/transaction" element={<Guarded roles={["owner"]}><SettingsTransaction /></Guarded>} />
            <Route path="/settings/print" element={<Guarded roles={["owner"]}><SettingsPrint /></Guarded>} />
            <Route path="/settings/print-templates" element={<Guarded roles={["owner"]}><PrintTemplates /></Guarded>} />
            <Route path="/settings/print-templates/new" element={<Guarded roles={["owner"]}><PrintTemplateForm /></Guarded>} />
            <Route path="/settings/print-templates/:tid/edit" element={<Guarded roles={["owner"]}><PrintTemplateForm /></Guarded>} />
            <Route path="/settings/backup" element={<Guarded roles={["owner"]}><OrgBackupPage /></Guarded>} />
            <Route path="/settings/local-server" element={<Guarded roles={["owner"]}><LocalServer /></Guarded>} />
            <Route path="/settings/restore" element={<Guarded roles={["owner"]}><OrgRestorePage /></Guarded>} />
            <Route path="/settings/notification" element={<Guarded roles={["owner"]}><SettingsNotification /></Guarded>} />
            <Route path="/settings/language" element={<Guarded roles={["owner"]}><SettingsLanguage /></Guarded>} />
            <Route path="/packages" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><Packages /></Guarded>} />
            <Route path="/customers" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><Customers /></Guarded>} />
            <Route path="/vehicles" element={<Vehicles />} />
            <Route path="/vehicles/:id" element={<VehicleDetail />} />
            <Route path="/parts" element={<Parts />} />
            <Route path="/labels" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><BarcodeLabels /></Guarded>} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/reminders" element={<Guarded roles={["owner", "manager", "cashier", "receptionist"]}><Reminders /></Guarded>} />
            <Route path="/insights" element={<Guarded roles={["owner", "manager"]}><AIInsights /></Guarded>} />
            <Route path="/portal" element={<CustomerPortal />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </I18nProvider>
      </MinimizedProvider>
    </AuthProvider>
  );
}

export default App;
